import React, { FC, useEffect, useState } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import { Box } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { toast } from 'react-toastify';
import _ from 'lodash';

import { Modal } from '@shared/components/Modal';
import { Button } from '@shared/components/Button';
import { ToastMessage } from '@shared/components/Toast';
import { useProcedureUIStore, useProceduresUIStore } from '@core/useStores';
import { ProcedureDevices } from '../ProcedureModal/components/ProcedureDevices';
import { ProcedureListingDevice } from 'Procedures/domain/Device';
import { IProcedureDeviceUI } from 'Procedures/stores';
import { messages } from 'Procedures/procedures.messages';
import { ThemeProps } from '@styles/theme';
import { useStyles } from './VersionUploadModal.styles';

export type VersionUploadModalProps = {
    isOpen: boolean;
    onRequestClose: () => void;
};

export const VersionUploadModal: FC<VersionUploadModalProps> = ({ isOpen, onRequestClose }) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const procedureUIStore = useProcedureUIStore();
    const proceduresGridUIStore = useProceduresUIStore();

    // Track initial device IDs to detect changes (add/remove device triggers dirty state)
    const [initialDeviceIds, setInitialDeviceIds] = useState<string[]>([]);
    const [, setIsDirty] = useState(false);
    const [deviceVersionLabels, setDeviceVersionLabels] = useState<Record<string, string>>({});

    useEffect(() => {
        const init = async () => {
            if (procedureUIStore.procedureToEditId) {
                procedureUIStore.toggleLoading(true);

                const result = await procedureUIStore.loadProcedure(procedureUIStore.procedureToEditId);

                procedureUIStore.toggleLoading(false);

                if (result.success) {
                    const preparedDevices = result.data.devices.map((device: ProcedureListingDevice) => {
                        const { id, name, platform, type, procedureVersionId, hasScoreData } = device;

                        return {
                            _device: {
                                device: name,
                                platform,
                                deviceId: id,
                                type,
                                procedureVersionId,
                                hasScoreData,
                            },
                            uiId: _.uniqueId('tab_'),
                        };
                    });

                    procedureUIStore.setProcedureGeneralInfo(result.data);
                    procedureUIStore.setDevices(preparedDevices);

                    // Snapshot initial device IDs to detect subsequent changes
                    const ids = preparedDevices
                        .map((d: any) => d._device.deviceId)
                        .filter(Boolean) as string[];
                    setInitialDeviceIds(ids);
                    setIsDirty(false);

                    // Build deviceId → version string map from grid store
                    const procedureFromGrid = proceduresGridUIStore.procedures.find(
                        p => p.id === procedureUIStore.procedureToEditId
                    );
                    if (procedureFromGrid) {
                        const labels: Record<string, string> = {};
                        for (const version of procedureFromGrid.versions) {
                            for (const device of version.devices) {
                                if (device.deviceId) {
                                    labels[device.deviceId] = version.version;
                                }
                            }
                        }
                        setDeviceVersionLabels(labels);
                    }
                }
            }

            procedureUIStore.getToken();
        };

        init();
    }, []);

    const handleSave = async () => {
        const info = procedureUIStore.procedureGeneralInfo;
        const deviceIds = _.compact(
            procedureUIStore.devices.map((d: IProcedureDeviceUI) => d?._device?.deviceId)
        );

        procedureUIStore.toggleLoading(true);

        const result = await procedureUIStore.editProcedure({
            id: procedureUIStore.procedureToEditId!,
            name: info?.name || '',
            description: info?.description || '',
            deviceIds,
            groupIds: info?.groupIds?.slice() || [],
            userIds: info?.userIds?.slice() || [],
            ownership: info?.ownership || '',
            token: procedureUIStore.processToken || '',
        });

        procedureUIStore.toggleLoading(false);

        if (result?.success) {
            toast.success(
                <ToastMessage message={messages['procedureModal.versionUpload.success']} type='success' />
            );
            proceduresGridUIStore.setParams({ page: 0 });
            proceduresGridUIStore.load();
            onRequestClose();
        } else {
            toast.error(
                <ToastMessage message={messages['procedureModal.versionUpload.error']} type='error' />
            );
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className={styles.modal}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc
        >
            <Observer>
                {() => (
                    <>
                        <div className={styles.header}>
                            <CloudUploadOutlinedIcon className={styles.headerIcon} />
                            <div className={styles.headerText}>
                                <h2 className={styles.title}>{messages['procedureModal.versionUpload.title']}</h2>
                                <p className={styles.subtitle}>
                                    {procedureUIStore.procedureGeneralInfo?.name || messages['procedureModal.loading']}
                                </p>
                            </div>
                            {(procedureUIStore.isTokenLoading || procedureUIStore.isLoading) && (
                                <CircularProgress size={20} className={styles.loader} />
                            )}
                        </div>

                        {
                            procedureUIStore.isLoading && !procedureUIStore.procedureGeneralInfo
                                ? (
                                    <Box className={styles.devicesContent} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Skeleton variant="rounded" width={160} height={32} />
                                        <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
                                    </Box>
                                )
                                : (
                                    <ProcedureDevices isEditMode={true} className={styles.devicesContent} deviceVersionLabels={deviceVersionLabels} />
                                )
                        }

                        <div className={styles.footer}>
                            <Button onClick={onRequestClose} color="secondary">
                                {messages['procedureModal.cancel']}
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={(() => {
                                    const currentIds = _.compact(
                                        procedureUIStore.devices.map((d: any) => d?._device?.deviceId)
                                    );
                                    const changed =
                                        currentIds.length !== initialDeviceIds.length ||
                                        !currentIds.every(id => initialDeviceIds.includes(id));

                                    return !changed || procedureUIStore.isNextStepDisabled || procedureUIStore.isLoading;
                                })()}
                            >
                                {messages['procedureModal.versionUpload.save']}
                            </Button>
                        </div>
                    </>
                )}
            </Observer>
        </Modal>
    );
};
